import DriveEtaOutlinedIcon from "@mui/icons-material/DriveEtaOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { UseQueryResult } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { createQuote } from "../../api/quote/createQuote";
import { getQuote } from "../../api/quote/getQuote";
import { getQuoteDealerPricingRequestCounts } from "../../api/quote/getQuoteDealerPricingRequestCounts";
import { PatchQuotePayload, updateQuote } from "../../api/quote/updateQuote";
import { getVehicleMakes } from "../../api/vehicle/getVehicleMakes";
import { getVehicleModels } from "../../api/vehicle/getVehicleModels";
import { getVehicleModelYears } from "../../api/vehicle/getVehicleModelYears";
import { getVehicleVariants } from "../../api/vehicle/getVehicleVariants";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import CatchEButton from "../../components/buttons/CatchEButton";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APForm from "../../components/form/APForm";
import APToggleGroup from "../../components/form/APFormToggleGroup";
import APTextField from "../../components/form/APTextField";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import {
  buildAutoCompleteOptions,
  findAutoCompleteOptionById,
  getAutoCompleteLabel,
} from "../../helpers/buildOptions";
import { trimStringInputs } from "../../helpers/form";
import {
  extractQuoteParameters,
  hasVehicleParameters,
} from "../../helpers/quoteParams";
import { useCascadeReset } from "../../hooks/useCascadeReset";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import { ErrorCode } from "../../types/Error";
import {
  internalOnlyPurchaseTypeOptions,
  Quote,
  VehiclePurchaseType,
  vehiclePurchaseTypeOptions,
} from "../../types/Quote";
import { termOptions } from "../../types/QuoteTermOptions";
import { State, stateOptions } from "../../types/State";
import { User } from "../../types/User";

export enum QuoteCreateAction {
  Create = "Create",
  Clone = "Clone",
  Edit = "Edit",
}

type Inputs = {
  purchaseType: AutoCompleteOption | null;
  vehicleMake: AutoCompleteOption | null;
  vehicleModel: AutoCompleteOption | null;
  vehicleYear: AutoCompleteOption | null;
  vehicleVariant: AutoCompleteOption | null;
  vehicleDiscount: string;
  vehiclePrice: string;
  initialOdometer: string;
  state: AutoCompleteOption | null;
  income: string;
  term: string;
  kilometres: string;
  businessUsage: string;
  listPrice: string;
  referralCode: string;
};

export type CreateQuoteSubmitAction = "next" | "personalise";
const CreateQuote = ({
  me,
  action,
}: {
  me: User;
  action: QuoteCreateAction;
}) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    unregister,
    formState: { errors },
  } = useForm<Inputs>({ mode: "onTouched" });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dossierId, quoteId } = useParams();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const [showReferralCodeField, setShowReferralCodeField] = useState(false);
  const [showDiscountField, setShowDiscountField] = useState(false);
  const { getScopedPageUrl, inAppScope } = useAuth();
  const hasSetQueryParams = useRef(false);

  const limitedFields =
    !!searchParams.get("limitedFields") &&
    searchParams.get("limitedFields") === "true";

  // Extract quote parameters using centralized helper
  const quoteParams = extractQuoteParameters(searchParams);
  const hasVehicleOverrides = hasVehicleParameters(quoteParams);

  const onNavigate = (
    quoteId: string,
    submitAction: CreateQuoteSubmitAction
  ) => {
    switch (submitAction) {
      case "next":
        navigate(getScopedPageUrl("viewQuote", quoteId));
        break;
      case "personalise":
        navigate(getScopedPageUrl("personaliseQuote", quoteId));
        break;
    }
  };

  let quote: UseQueryResult<Quote, unknown> | null = null;
  const pricingRequestCounts = getQuoteDealerPricingRequestCounts(
    quoteId ?? "",
    {
      disabled: !quoteId,
    }
  );

  switch (action) {
    case QuoteCreateAction.Create:
      if (!navigationCheckComplete) {
        // Handle purchase type from query parameters
        if (quoteParams.purchaseType) {
          const purchaseTypeOption = vehiclePurchaseTypeOptions.find(
            (type) => type.id === quoteParams.purchaseType
          );
          if (purchaseTypeOption) {
            setValue("purchaseType", purchaseTypeOption);
          }
        } else {
          // Default to "new" if no purchase type specified
          const newPurchaseType = vehiclePurchaseTypeOptions.find(
            (type) => type.id === "new"
          );
          if (newPurchaseType) {
            setValue("purchaseType", newPurchaseType);
          }
        }

        if (quoteParams.term) {
          setValue("term", quoteParams.term);
        }

        if (quoteParams.salary) {
          setValue("income", quoteParams.salary);
        }

        if (quoteParams.businessUsage) {
          setValue("businessUsage", quoteParams.businessUsage);
        }

        if (quoteParams.discount) {
          setValue("vehicleDiscount", quoteParams.discount);
        }

        if (quoteParams.listPrice) {
          setValue("listPrice", quoteParams.listPrice);
        }

        setNavigationCheck(true);
      }
      break;
    case QuoteCreateAction.Clone:
    case QuoteCreateAction.Edit:
      if (!quoteId) {
        // TODO: improve client side error messaging
        throw new Error("quote id param is misisng");
      }

      quote = getQuote(quoteId, {
        mutationOptions: {
          errorNavPaths: { [ErrorCode.QuoteDeleted]: getScopedPageUrl("home") },
        },
      });

      useEffect(() => {
        if (quote?.isSuccess) {
          // If the quote is locked and the action is edit then redirect to the quote page
          if (quote.data.config.isLocked && action === QuoteCreateAction.Edit) {
            navigate(getScopedPageUrl("viewQuote", quoteId), {
              replace: true,
            });
          } else {
            // Set purchase type from query parameter if present, otherwise use quote value
            if (quoteParams.purchaseType) {
              const purchaseTypeOption = vehiclePurchaseTypeOptions.find(
                (type) => type.id === quoteParams.purchaseType
              );
              if (purchaseTypeOption) {
                setValue("purchaseType", purchaseTypeOption);
              }
            } else if (
              !quoteParams.aiQueryId &&
              !quoteParams.vehicleReviewAIQueryId &&
              !quoteParams.vehicleSearchEventId &&
              !quoteParams.vehicleComparisonViewId
            ) {
              setValue("purchaseType", {
                id: quote.data.vehicle.purchaseType,
                label: getAutoCompleteLabel(
                  vehiclePurchaseTypeOptions,
                  quote.data.vehicle.purchaseType
                ),
              });
            } else {
              const newPurchaseType = vehiclePurchaseTypeOptions.find(
                (option) => option.id === ("new" as VehiclePurchaseType)
              );
              if (newPurchaseType) {
                setValue("purchaseType", newPurchaseType);
              }
            }

            if (!hasVehicleOverrides && quote.data.vehicle.makeId) {
              setValue("vehicleMake", {
                id: quote.data.vehicle.makeId,
                label: `${quote.data.vehicle.make}`,
              });
            }

            if (!hasVehicleOverrides && quote.data.vehicle.modelId) {
              setValue("vehicleModel", {
                id: quote.data.vehicle.modelId,
                label: `${quote.data.vehicle.model}`,
              });
            }

            if (!hasVehicleOverrides && quote.data.vehicle.year) {
              setValue("vehicleYear", {
                id: quote.data.vehicle.year,
                label: quote.data.vehicle.year,
              });
            }

            if (!hasVehicleOverrides && quote.data.vehicle.variantId) {
              setValue("vehicleVariant", {
                id: quote.data.vehicle.variantId,
                label: quote.data.vehicle.variantName,
              });
            }

            // Handle discount override
            if (quoteParams.discount) {
              setValue("vehicleDiscount", quoteParams.discount);
            } else if (
              quote.data.vehicle.discount &&
              !quoteParams.isCreateUrl
            ) {
              setValue("vehicleDiscount", `${quote.data.vehicle.discount}`);
            }

            setValue(
              "initialOdometer",
              `${quote.data.vehicle.initialOdometer}`
            );

            // Handle list price override
            if (quoteParams.listPrice) {
              setValue("listPrice", quoteParams.listPrice);
            } else if (quote.data.vehicle.listPrice) {
              setValue("listPrice", `${quote.data.vehicle.listPrice.incGst}`);
            }

            setValue("state", {
              id: quote.data.vehicle.state,
              label: quote.data.vehicle.state,
            });

            // Handle salary override
            if (quoteParams.salary) {
              setValue("income", quoteParams.salary);
            } else {
              setValue(
                "income",
                `${quote.data.salaryComparison.salaryExSuper}`
              );
            }

            // Handle term override
            if (quoteParams.term) {
              setValue("term", quoteParams.term);
            } else {
              setValue("term", `${quote.data.term.termMonths}`);
            }

            setValue("kilometres", `${quote.data.vehicle.budgetedAnnualKm}`);

            // Handle business usage override
            if (quoteParams.businessUsage) {
              setValue("businessUsage", quoteParams.businessUsage);
            } else if (quote.data.vehicle.businessUsePercentage) {
              setValue(
                "businessUsage",
                `${quote.data.vehicle.businessUsePercentage}`
              );
            }

            setNavigationCheck(true);
          }
        }
      }, [quote.isSuccess]);

      if (quote.isError) {
        throw quote.error;
      }
      break;
  }

  let _dossierId: string = "";

  if (action === QuoteCreateAction.Create) {
    if (!dossierId) {
      // TODO: improve client side error messaging
      throw new Error("dossier id param is misisng");
    }
    _dossierId = dossierId;
  } else if (
    action === QuoteCreateAction.Clone ||
    action === QuoteCreateAction.Edit
  ) {
    _dossierId = quote?.data?.identifiers.crmDossierId || "";
  }

  const dossier = getDossier(_dossierId, { disabled: !_dossierId });
  const { isLoading: isLoadingFaqs, isSuccess: isSuccessFaqs } = useFaqs({
    pageId: "quote-edit",
    contextIds: {
      quoteId: quoteId,
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const configMap = getPublicConfig();

  if (dossier.isError) {
    throw dossier.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }

  const { mutateAsync, isLoading: isLoadingMutate } = createQuote(_dossierId);

  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdateMutate } =
    updateQuote(quoteParams.overwriteQuote ?? quoteId ?? "", {
      mutationOptions: {
        errorNavPaths: {
          [ErrorCode.QuoteLocked]: getScopedPageUrl("viewQuote", quoteId),
        },
        errorReplaceHistory: true,
      },
    });

  const response = getVehicleMakes();

  if (response.isError) {
    throw response.error;
  }

  const buildCreatePayload = (data: Inputs) => {
    if (!data.purchaseType || !data.state || !data.vehicleVariant) {
      // TODO: better error
      throw new Error("invalid");
    }

    return {
      purchaseType: data.purchaseType.id as VehiclePurchaseType,
      state: data.state.id as State,
      variantId: data.vehicleVariant.id,
      salary: parseFloat(data.income),
      term: parseInt(data.term),
      annualKilometres: parseInt(data.kilometres),
      businessUsage:
        limitedFields || !data.businessUsage ? 0 : parseInt(data.businessUsage),
      discount: data.vehicleDiscount ? parseFloat(data.vehicleDiscount) : 0,
      initialOdometer: data.initialOdometer
        ? parseInt(data.initialOdometer)
        : 0,
      listPrice: data.listPrice ? parseFloat(data.listPrice) : undefined,
      referralCode: data.referralCode || undefined,
      aiQueryId: quoteParams.aiQueryId ?? undefined,
      vehicleReviewAIQueryId: quoteParams.vehicleReviewAIQueryId ?? undefined,
      vehicleSearchEventId: quoteParams.vehicleSearchEventId ?? undefined,
      vehicleComparisonViewId: quoteParams.vehicleComparisonViewId ?? undefined,
    };
  };

  const buildUpdatePayload = (data: Inputs, quote: Quote) => {
    if (!data.purchaseType || !data.state || !data.vehicleVariant) {
      // TODO: better error
      throw new Error("invalid");
    }

    const updatePayload: PatchQuotePayload = {
      aiQueryId: quoteParams.aiQueryId ?? undefined,
      vehicleReviewAIQueryId: quoteParams.vehicleReviewAIQueryId ?? undefined,
      vehicleSearchEventId: quoteParams.vehicleSearchEventId ?? undefined,
      vehicleComparisonViewId: quoteParams.vehicleComparisonViewId ?? undefined,
    };

    if (
      data.purchaseType.id !== quote.vehicle.purchaseType ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.purchaseType = data.purchaseType.id as VehiclePurchaseType;
    }

    if (
      data.vehicleVariant.id !== quote.vehicle.variantId ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.variantId = data.vehicleVariant.id;
    }

    if (
      (data.vehicleDiscount || "0") !== `${quote.vehicle.discount}` ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.discount = data.vehicleDiscount
        ? parseFloat(data.vehicleDiscount)
        : 0;
    }
    if (
      (data.initialOdometer || "0") !== `${quote.vehicle.initialOdometer}` ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.initialOdometer = data.initialOdometer
        ? parseInt(data.initialOdometer)
        : 0;
    }
    if (
      data.listPrice &&
      (data.listPrice !== `${quote.vehicle.listPrice.incGst}` ||
        quoteParams.overwriteQuote)
    ) {
      updatePayload.listPrice = parseFloat(data.listPrice);
    }

    if (data.state.id !== quote.vehicle.state || quoteParams.overwriteQuote) {
      updatePayload.state = data.state.id as State;
    }
    if (
      data.term !== `${quote.term.termMonths}` ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.term = parseInt(data.term);
    }
    if (
      data.kilometres !== `${quote.vehicle.budgetedAnnualKm}` ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.annualKilometres = parseInt(data.kilometres);
    }
    if (
      data.income !== `${quote.salaryComparison.salaryExSuper}` ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.salary = parseFloat(data.income);
    }
    if (
      (data.businessUsage || "0") !==
        `${quote.vehicle.businessUsePercentage}` ||
      quoteParams.overwriteQuote
    ) {
      updatePayload.businessUsage = data.businessUsage
        ? parseInt(data.businessUsage)
        : 0;
    }

    if (data.referralCode) {
      updatePayload.referralCode = data.referralCode;
    }

    return updatePayload;
  };

  const onSubmit = async (
    _data: Inputs,
    submitAction: CreateQuoteSubmitAction
  ) => {
    trimStringInputs(_data, setValue);
    const data = getValues();

    if (!quote || quote.isSuccess) {
      const isCreate =
        (action === QuoteCreateAction.Create ||
          action === QuoteCreateAction.Clone) &&
        !quoteParams.overwriteQuote;
      const isEdit =
        action === QuoteCreateAction.Edit || quoteParams.overwriteQuote;
      const _quoteId = quoteParams.overwriteQuote ?? quoteId;

      if (isCreate) {
        await mutateAsync(buildCreatePayload(data), {
          onSuccess: (quoteId: string) => onNavigate(quoteId, submitAction),
        });
      } else if (isEdit && quote && _quoteId) {
        const updatePayload: PatchQuotePayload = buildUpdatePayload(
          data,
          quote.data
        );

        if (Object.keys(updatePayload).length > 0) {
          updatePayload.dossierId = _dossierId;
          await mutateAsyncUpdate(updatePayload);
        }
        onNavigate(_quoteId, submitAction);
      }
    }
  };

  const onNext: SubmitHandler<Inputs> = async (data) => {
    onSubmit(data, "next");
  };

  const onPersonalise: SubmitHandler<Inputs> = async (data) => {
    onSubmit(data, "personalise");
  };

  const onReferralLinkClick = () => {
    setShowReferralCodeField(true);
  };

  const onDiscountLinkClick = () => {
    setShowDiscountField(true);
  };

  const watchPurchaseType = watch("purchaseType");
  const watchVehicleMake = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleMake",
    childField: "vehicleModel",
  });
  const watchVehicleModel = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleModel",
    childField: "vehicleYear",
  });
  const watchVehicleYear = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleYear",
    childField: "vehicleVariant",
  });
  const watchVehicleVariant = watch("vehicleVariant");
  const watchDiscount = watch("vehicleDiscount");
  const watchlistPrice = watch("listPrice");

  useEffect(() => {
    const previous = quote && quote.isSuccess && quote.data.vehicle.variantId;
    const current = watchVehicleVariant && watchVehicleVariant.id;

    if (!!current && previous !== current) {
      setValue("listPrice", "");
    }
  }, [watchVehicleVariant]);

  useEffect(() => {
    if (watchPurchaseType) {
      if (watchPurchaseType.id === "new") {
        if (inAppScope("external") && !quoteParams.listPrice) {
          setValue("listPrice", "");
        } else {
          unregister("listPrice");
          setValue("listPrice", watchlistPrice);
        }
        setValue("initialOdometer", "");
      } else {
        setValue("vehicleDiscount", "");
        setShowDiscountField(false);
      }
    }
  }, [watchPurchaseType]);

  useEffect(() => {
    if (watchDiscount && !showDiscountField) {
      setShowDiscountField(true);
    }
  }, [watchDiscount]);

  const vehicleModels = getVehicleModels(watchVehicleMake?.id || null, {
    disabled: !watchVehicleMake,
  });
  const vehicleModelYears = getVehicleModelYears(
    watchVehicleModel?.id || null,
    {
      disabled: !watchVehicleModel,
    }
  );

  const vehicleModelVariants = getVehicleVariants(
    watchVehicleMake?.id || null,
    watchVehicleModel?.id || null,
    watchVehicleYear?.id || null,
    {
      disabled: !watchVehicleMake || !watchVehicleModel || !watchVehicleYear,
    }
  );

  // Handle query parameters for all actions after data is loaded
  useEffect(() => {
    // Only handle query parameters if we have vehicle overrides and haven't set them yet
    const shouldHandleQueryParams =
      hasVehicleOverrides && !hasSetQueryParams.current;

    if (shouldHandleQueryParams) {
      let hasSetAnyValue = false;

      // Handle make from query parameters
      if (quoteParams.makeId && response.isSuccess && response.data) {
        const makeOption = findAutoCompleteOptionById(
          response.data,
          "id",
          "name",
          quoteParams.makeId
        );
        if (makeOption) {
          setValue("vehicleMake", makeOption);
          hasSetAnyValue = true;
        }
      }

      // Handle model from query parameters (after make is set)
      if (
        quoteParams.modelId &&
        vehicleModels.isSuccess &&
        vehicleModels.data
      ) {
        const modelOption = findAutoCompleteOptionById(
          vehicleModels.data,
          "id",
          "name",
          quoteParams.modelId
        );
        if (modelOption) {
          setValue("vehicleModel", modelOption);
          hasSetAnyValue = true;
        }
      }

      // Handle year from query parameters
      if (quoteParams.modelYear) {
        setValue("vehicleYear", {
          id: quoteParams.modelYear,
          label: quoteParams.modelYear,
        });
        hasSetAnyValue = true;
      }

      // Handle variant from query parameters (after year is set)
      if (
        quoteParams.variantId &&
        vehicleModelVariants.isSuccess &&
        vehicleModelVariants.data
      ) {
        const variantOption = findAutoCompleteOptionById(
          vehicleModelVariants.data,
          "id",
          "name",
          quoteParams.variantId
        );
        if (variantOption) {
          setValue("vehicleVariant", variantOption);
          hasSetAnyValue = true;
        }
      }

      // Only mark as complete if we've processed all available query parameters
      const hasAllRequiredData =
        (!quoteParams.makeId || response.isSuccess) &&
        (!quoteParams.modelId || vehicleModels.isSuccess) &&
        (!quoteParams.variantId || vehicleModelVariants.isSuccess);

      if (hasAllRequiredData && hasSetAnyValue) {
        hasSetQueryParams.current = true;
      }
    }
  }, [
    hasVehicleOverrides,
    response.isSuccess,
    vehicleModels.isSuccess,
    vehicleModelVariants.isSuccess,
  ]);

  const isLoading =
    response.isFetching ||
    (!!quote && quote.isFetching) ||
    !_dossierId ||
    dossier.isFetching ||
    isLoadingFaqs ||
    configMap.isFetching ||
    pricingRequestCounts.isFetching;
  const isSuccess =
    response.isSuccess &&
    (!quote || quote.isSuccess) &&
    dossier.isSuccess &&
    isSuccessFaqs &&
    configMap.isSuccess &&
    (!quoteId || pricingRequestCounts.isSuccess);
  const showReferralCodeLink =
    !showReferralCodeField &&
    dossier?.data?.opportunity &&
    !dossier.data.opportunity.referralCode;
  const showDiscountLink = !showDiscountField && watchVehicleVariant;
  const hasPricingRequests =
    action === QuoteCreateAction.Edit &&
    pricingRequestCounts.isSuccess &&
    pricingRequestCounts.data.total > 0;
  const isVehicleConfigurationLocked =
    (action === QuoteCreateAction.Edit &&
      quote?.data?.config?.isVehicleConfigurationLocked) ||
    false;

  return (
    <PageContainer
      loading={isLoading || !navigationCheckComplete}
      title="Select Vehicle"
    >
      {isSuccess && navigationCheckComplete && (
        <>
          <Typography variant="labelLarge" color="onSurface.main" gutterBottom>
            This info is needed for your quote.
          </Typography>

          <APForm
            onSubmit={handleSubmit(onNext)}
            submitText="View Quote"
            submitIcon={<PlayArrowOutlined />}
            isLoading={isLoadingMutate || isLoadingUpdateMutate}
            isError={Object.keys(errors).length > 0}
            leftButton={
              <RestrictedAppScope scope="external">
                <Box sx={{ display: "flex", justifyContent: "start" }}>
                  <APContactHelpButton
                    regardingOpportunityId={dossier.data?.opportunity?.id}
                  />
                </Box>
              </RestrictedAppScope>
            }
            additionalButton={
              <Box sx={{ display: "flex" }}>
                {quote?.data && (
                  <Box sx={{ mr: { md: 2, sm: 0 } }}>
                    <RestrictedAppScope scope="internal">
                      <CatchEButton
                        url={quote.data.identifiers.catchEQuoteUrl}
                      />
                    </RestrictedAppScope>
                  </Box>
                )}
                <Box>
                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    disableElevation={true}
                    startIcon={<DriveEtaOutlinedIcon />}
                    onClick={handleSubmit(onPersonalise)}
                  >
                    Options
                  </Button>
                </Box>
              </Box>
            }
            mobileButtonPosition="left"
          >
            <APAutoComplete
              name="purchaseType"
              label="Purchase Type"
              options={[
                ...vehiclePurchaseTypeOptions,
                ...(inAppScope("internal")
                  ? internalOnlyPurchaseTypeOptions
                  : internalOnlyPurchaseTypeOptions.filter(
                      (option) =>
                        watchPurchaseType && option.id === watchPurchaseType.id
                    )),
              ]}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
              disabled={hasPricingRequests || isVehicleConfigurationLocked}
            />

            <APAutoComplete
              name="vehicleMake"
              label="Vehicle Make"
              options={buildAutoCompleteOptions(response.data, "name", "id")}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
              disabled={hasPricingRequests || isVehicleConfigurationLocked}
            />

            <APAutoComplete
              name="vehicleModel"
              label="Vehicle Model"
              options={
                vehicleModels.isSuccess
                  ? buildAutoCompleteOptions(vehicleModels.data, "name", "id")
                  : []
              }
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
              disabled={
                !watchVehicleMake ||
                !vehicleModels.isSuccess ||
                hasPricingRequests ||
                isVehicleConfigurationLocked
              }
            />

            <APAutoComplete
              name="vehicleYear"
              label="Vehicle Year"
              options={
                vehicleModelYears.isSuccess
                  ? buildAutoCompleteOptions(
                      vehicleModelYears.data.map((year) => {
                        return { id: `${year}`, name: `${year}` };
                      }),
                      "name",
                      "id"
                    )
                  : []
              }
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
              disabled={
                !watchVehicleMake ||
                !watchVehicleModel ||
                !vehicleModelYears.isSuccess ||
                hasPricingRequests ||
                isVehicleConfigurationLocked
              }
            />

            <APAutoComplete
              name="vehicleVariant"
              label="Vehicle Variant"
              options={
                vehicleModelVariants.isSuccess
                  ? buildAutoCompleteOptions(
                      vehicleModelVariants.data,
                      "name",
                      "id"
                    )
                  : []
              }
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
              disabled={
                !watchVehicleMake ||
                !watchVehicleModel ||
                !watchVehicleYear ||
                !vehicleModelVariants.isSuccess ||
                hasPricingRequests ||
                isVehicleConfigurationLocked
              }
              highlightOnEmpty={!!quoteParams.aiQueryId}
            />

            {watchPurchaseType && watchPurchaseType.id === "new" && (
              <>
                {showDiscountField && (
                  <APTextField<Inputs>
                    name="vehicleDiscount"
                    label="Vehicle Discount Inc GST"
                    control={control}
                    validations={{
                      formatValidation: /^[0-9]+(\.[0-9]{1,2})?$/,
                    }}
                    errors={errors}
                    defaultValue={""}
                    startAdornment="$"
                    placeholder="xxxxxxx.xx"
                    formatNumber={true}
                    disabled={isVehicleConfigurationLocked}
                  />
                )}
                {showDiscountLink && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      mt: "12px !important",
                    }}
                  >
                    <Typography
                      variant="bodySmall"
                      color="primary"
                      onClick={onDiscountLinkClick}
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                      I have a discount
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {watchPurchaseType &&
              (watchPurchaseType.id !== "new" || inAppScope("internal")) && (
                <APTextField<Inputs>
                  name="listPrice"
                  label="Vehicle Price Inc GST"
                  control={control}
                  validations={{
                    required: watchPurchaseType.id !== "new",
                    formatValidation: /[0-9]+(\.[0-9][0-9]?)?/,
                  }}
                  errors={errors}
                  defaultValue=""
                  startAdornment="$"
                  placeholder="xxxxxxx.xx"
                  formatNumber={true}
                  disabled={isVehicleConfigurationLocked}
                />
              )}

            {watchPurchaseType && watchPurchaseType.id !== "new" && (
              <APTextField<Inputs>
                name="initialOdometer"
                label="Initial Odometer"
                control={control}
                validations={{
                  required: true,
                  formatValidation: /(?!0+$)[0-9]+/,
                  customMessages: {
                    formatValidation: "Value must be a number greater than 0",
                  },
                }}
                errors={errors}
                defaultValue=""
                endAdornment="km"
                placeholder="xxxxx"
                disabled={isVehicleConfigurationLocked}
              />
            )}

            <APAutoComplete
              name="state"
              label="State"
              options={stateOptions}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={""}
              disabled={hasPricingRequests || isVehicleConfigurationLocked}
            />
            <APTextField<Inputs>
              name="income"
              label="Annual Salary (ex super)"
              control={control}
              validations={{
                required: true,
                formatValidation: /^[0-9]+(\.[0-9]{1,2})?$/,
              }}
              errors={errors}
              defaultValue={""}
              startAdornment="$"
              placeholder="xxxxxxx.xx"
              formatNumber={true}
              disabled={isVehicleConfigurationLocked}
            />
            <APToggleGroup
              name="term"
              control={control}
              options={termOptions}
              defaultValue="60"
              readOnly={isVehicleConfigurationLocked}
            />
            <APTextField<Inputs>
              name="kilometres"
              label="Annual Km"
              control={control}
              validations={{
                required: true,
                formatValidation: /[0-9]+/,
                maxValue: 500000,
              }}
              errors={errors}
              defaultValue="10000"
              endAdornment="km"
              placeholder="xxxxx"
              helperText="Number of kms driven p.a."
              disabled={hasPricingRequests || isVehicleConfigurationLocked}
            />
            {!limitedFields && (
              <APTextField<Inputs>
                name="businessUsage"
                label="Business Usage (if any)"
                control={control}
                validations={{
                  formatValidation: /^(100|[1-9]?[0-9])$/,
                  customMessages: {
                    formatValidation:
                      "Value must be a number between 0 and 100",
                  },
                }}
                errors={errors}
                defaultValue=""
                endAdornment="%"
                placeholder="xx"
                helperText="Business logbook required"
                disabled={isVehicleConfigurationLocked}
              />
            )}
            <RestrictedAppScope scope="external">
              {showReferralCodeField && (
                <APTextField<Inputs>
                  name="referralCode"
                  label="Referral Code"
                  control={control}
                  validations={{
                    maxLength: 10,
                    minLength: 6,
                    formatValidation:
                      me && me.referralCode
                        ? new RegExp(`^(?!${me.referralCode}$).*$`, "i")
                        : undefined,
                    customMessages: {
                      formatValidation:
                        "You cannot use your own referral code.",
                    },
                  }}
                  errors={errors}
                  defaultValue=""
                />
              )}
              {showReferralCodeLink && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    mt: "12px !important",
                  }}
                >
                  <Typography
                    variant="bodySmall"
                    color="primary"
                    onClick={onReferralLinkClick}
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    I have a referral code
                  </Typography>
                </Box>
              )}
            </RestrictedAppScope>
          </APForm>
        </>
      )}
    </PageContainer>
  );
};

export default CreateQuote;
